import type { ITooltipParams } from 'ag-grid-community';
import type { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { AgGridDefaultHeaderTooltip } from '../../AgGrid/AgGridDefaultHeaderTooltip';
import { Box } from '../../Core';
import { PopoverContent } from '../../Popover/PopoverContent';
import { Text } from '../../Text';

/** Registered name for {@link AgGridBlotterTooltip} on {@link AgGridReact} `components`. */
export const AG_GRID_BLOTTER_TOOLTIP_KEY = 'blotterTooltip' as const;

/** Non-empty placeholder so AG Grid instantiates header tooltips when only `richHeaderTooltip` is set.
 * - This feels like odd behavior, but we never had a header tooltip component before, and it was proven AG Grid behaves weirdly without
 * - see 'renders rich header tooltip content when no plain description is set' test in BlotterTable.cy.tsx
 */
export const AG_GRID_RICH_HEADER_TOOLTIP_SENTINEL = ' ';

export type AgGridBlotterTooltipComponentParams = {
  richHeaderTooltip?: ReactElement;
};

/** Params received by the tooltip component: AG Grid merges `colDef.tooltipComponentParams` onto the root tooltip params. */
type MergedBlotterTooltipParams = ITooltipParams & Partial<AgGridBlotterTooltipComponentParams>;

const MultilineTooltipText = styled(Text)`
  white-space: pre-wrap;
`;

function renderCellContent(value: unknown): ReactNode {
  if (typeof value !== 'string' || value === '') {
    return null;
  }
  return <MultilineTooltipText color="colorTextImportant">{value}</MultilineTooltipText>;
}

function isHeaderLikeTooltipLocation(location: ITooltipParams['location']): boolean {
  return location === 'header' || location === 'headerGroup';
}

/**
 * Default blotter column tooltip: plain header strings via {@link AgGridDefaultHeaderTooltip}, React header content via
 * {@link AgGridBlotterTooltipComponentParams.richHeaderTooltip}, and cell values from `tooltipField` /
 * `tooltipValueGetter`.
 *
 * @remarks AG Grid types tooltip components as {@link ITooltipParams} only, but at runtime it deep-merges
 * `colDef.tooltipComponentParams` onto the root params object (see {@link MergedBlotterTooltipParams}). The cast below
 * bridges that gap.
 */
export function AgGridBlotterTooltip(props: ITooltipParams): ReactNode {
  const params = props as MergedBlotterTooltipParams;
  const richHeaderTooltip = params.richHeaderTooltip;

  if (isHeaderLikeTooltipLocation(params.location)) {
    if (richHeaderTooltip != null) {
      return (
        <PopoverContent>
          <Box maxWidth="300px">{richHeaderTooltip}</Box>
        </PopoverContent>
      );
    }
    return <AgGridDefaultHeaderTooltip params={params} />;
  }

  // AG Grid puts tooltipValueGetter output in value while retaining the cell formatter output in valueFormatted.
  const hasTooltipValueGetter =
    params.colDef != null && 'tooltipValueGetter' in params.colDef && params.colDef.tooltipValueGetter != null;
  const cellDisplay = hasTooltipValueGetter ? params.value : (params.valueFormatted ?? params.value);
  const cellContent = renderCellContent(cellDisplay);
  if (cellContent == null) {
    return null;
  }

  return (
    <PopoverContent>
      <Box maxWidth="300px">{cellContent}</Box>
    </PopoverContent>
  );
}
